import { __decorate, __extends } from "tslib";
import { attachShadowTemplate, coerceBoolean, coerceNumber, CustomElement, FoundationProperty, upgradeProperty } from '@tyler-components-web/core';
import { ButtonComponent } from '../button';
import { IconButtonComponent } from '../icon-button';
import { ListComponent } from '../list';
import { ViewSwitcherComponent } from '../view-switcher';
import { CalendarAdapter } from './calendar-adapter';
import { CALENDAR_CONSTANTS } from './calendar-constants';
import { CalendarFoundation } from './calendar-foundation';
import { IconComponent, TylerIconRegistry } from '../icon';
import { tylIconAdd, tylIconKeyboardArrowLeft, tylIconKeyboardArrowRight, tylIconLens } from '@tylertech/tyler-icons/standard';
import { DividerComponent } from '../divider';
var template = require('./calendar.html');
var styles = require('./calendar.css');
var CalendarComponent = /** @class */ (function (_super) {
    __extends(CalendarComponent, _super);
    function CalendarComponent() {
        var _this = _super.call(this) || this;
        TylerIconRegistry.define([tylIconKeyboardArrowLeft, tylIconKeyboardArrowRight, tylIconLens, tylIconAdd]);
        attachShadowTemplate(_this, CALENDAR_CONSTANTS.elementName, template, styles);
        _this._foundation = new CalendarFoundation(new CalendarAdapter(_this));
        return _this;
    }
    Object.defineProperty(CalendarComponent, "observedAttributes", {
        get: function () {
            return [
                CALENDAR_CONSTANTS.attributes.MONTH,
                CALENDAR_CONSTANTS.attributes.YEAR,
                CALENDAR_CONSTANTS.attributes.SHOW_TODAY,
                CALENDAR_CONSTANTS.attributes.MULTISELECT,
                CALENDAR_CONSTANTS.attributes.MIN_DATE,
                CALENDAR_CONSTANTS.attributes.MAX_DATE,
                CALENDAR_CONSTANTS.attributes.MIN_YEAR,
                CALENDAR_CONSTANTS.attributes.MAX_YEAR,
                CALENDAR_CONSTANTS.attributes.PREVENT_FOCUS,
                CALENDAR_CONSTANTS.attributes.AUTO_SWITCH,
                CALENDAR_CONSTANTS.attributes.RANGE,
                CALENDAR_CONSTANTS.attributes.MODE,
                CALENDAR_CONSTANTS.attributes.SHOW_HEADER
            ];
        },
        enumerable: true,
        configurable: true
    });
    CalendarComponent.prototype.initializedCallback = function () {
        upgradeProperty(this, 'value');
        upgradeProperty(this, 'month');
        upgradeProperty(this, 'year');
        upgradeProperty(this, 'showToday');
        upgradeProperty(this, 'multiselect');
        upgradeProperty(this, 'minDate');
        upgradeProperty(this, 'maxDate');
        upgradeProperty(this, 'minYear');
        upgradeProperty(this, 'maxYear');
        upgradeProperty(this, 'blockedDates');
        upgradeProperty(this, 'preventFocus');
        upgradeProperty(this, 'autoSwitch');
        upgradeProperty(this, 'range');
        upgradeProperty(this, 'mode');
        upgradeProperty(this, 'showHeader');
        upgradeProperty(this, 'events');
        upgradeProperty(this, 'dateSelectCallback');
        upgradeProperty(this, 'disabledDaysOfWeek');
        upgradeProperty(this, 'disableDayCallback');
        upgradeProperty(this, 'eventsCallback');
        upgradeProperty(this, 'renderDateCallback');
    };
    CalendarComponent.prototype.connectedCallback = function () {
        this._foundation.initialize();
    };
    CalendarComponent.prototype.disconnectedCallback = function () {
        this._foundation.disconnect();
    };
    CalendarComponent.prototype.attributeChangedCallback = function (name, oldValue, newValue) {
        switch (name) {
            case CALENDAR_CONSTANTS.attributes.MONTH:
                this.month = coerceNumber(newValue);
                break;
            case CALENDAR_CONSTANTS.attributes.YEAR:
                this.year = coerceNumber(newValue);
                break;
            case CALENDAR_CONSTANTS.attributes.SHOW_TODAY:
                this.showToday = this.hasAttribute(CALENDAR_CONSTANTS.attributes.SHOW_TODAY);
                break;
            case CALENDAR_CONSTANTS.attributes.MULTISELECT:
                this.multiselect = this.hasAttribute(CALENDAR_CONSTANTS.attributes.MULTISELECT);
                break;
            case CALENDAR_CONSTANTS.attributes.MIN_DATE:
                if (!newValue) {
                    this.minDate = null;
                }
                else {
                    this.minDate = new Date(newValue);
                }
                break;
            case CALENDAR_CONSTANTS.attributes.MAX_DATE:
                if (!newValue) {
                    this.maxDate = null;
                }
                else {
                    this.maxDate = new Date(newValue);
                }
                break;
            case CALENDAR_CONSTANTS.attributes.MIN_YEAR:
                this.minYear = coerceNumber(newValue);
                break;
            case CALENDAR_CONSTANTS.attributes.MAX_YEAR:
                this.maxYear = coerceNumber(newValue);
                break;
            case CALENDAR_CONSTANTS.attributes.PREVENT_FOCUS:
                this.preventFocus = this.hasAttribute(CALENDAR_CONSTANTS.attributes.PREVENT_FOCUS);
                break;
            case CALENDAR_CONSTANTS.attributes.AUTO_SWITCH:
                this.autoSwitch = this.hasAttribute(CALENDAR_CONSTANTS.attributes.AUTO_SWITCH);
                break;
            case CALENDAR_CONSTANTS.attributes.RANGE:
                this.range = this.hasAttribute(CALENDAR_CONSTANTS.attributes.RANGE);
                break;
            case CALENDAR_CONSTANTS.attributes.MODE:
                this.mode = newValue;
                break;
            case CALENDAR_CONSTANTS.attributes.SHOW_HEADER:
                this.showHeader = coerceBoolean(newValue);
        }
    };
    Object.defineProperty(CalendarComponent.prototype, "month", {
        /** Gets and sets the displayed month. */
        get: function () {
            return this._foundation.month;
        },
        set: function (value) {
            this._foundation.month = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "year", {
        /** Gets and sets the displayed year. */
        get: function () {
            return this._foundation.year;
        },
        set: function (value) {
            this._foundation.year = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "value", {
        /** Gets and sets the selected dates. */
        get: function () {
            return this._foundation.selectedDates;
        },
        set: function (value) {
            this._foundation.selectedDates = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "showToday", {
        /** Controls whether today's date is highlighted */
        get: function () {
            return this._foundation.showToday;
        },
        set: function (value) {
            this._foundation.showToday = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "multiselect", {
        /** Sets whether multiple dates can be selected */
        get: function () {
            return this._foundation.multiselect;
        },
        set: function (value) {
            this._foundation.multiselect = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "minDate", {
        /** Gets and sets the earliest pickable date */
        get: function () {
            return this._foundation.minDate;
        },
        set: function (value) {
            this._foundation.minDate = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "maxDate", {
        /** Gets and sets the latest pickable date */
        get: function () {
            return this._foundation.maxDate;
        },
        set: function (value) {
            this._foundation.maxDate = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "minYear", {
        /** Gets and sets the minimum pickable year */
        get: function () {
            return this._foundation.minYear;
        },
        set: function (value) {
            this._foundation.minYear = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "maxYear", {
        /** Gets and sets the maximum pickable year */
        get: function () {
            return this._foundation.maxYear;
        },
        set: function (value) {
            this._foundation.maxYear = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "blockedDates", {
        /** Gets and sets the disabled dates */
        get: function () {
            return this._foundation.blockedDates;
        },
        set: function (value) {
            this._foundation.blockedDates = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "preventFocus", {
        /** Ignores mousedown events if true to avoid allowing internal elements to receive focus. */
        get: function () {
            return this._foundation.preventFocus;
        },
        set: function (value) {
            this._foundation.preventFocus = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "autoSwitch", {
        /** Tells the calendar to keep the most recent selected date in view. */
        get: function () {
            return this._foundation.autoSwitch;
        },
        set: function (value) {
            this._foundation.autoSwitch = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "range", {
        /** Allows range selection */
        get: function () {
            return this._foundation.range;
        },
        set: function (value) {
            this._foundation.range = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "mode", {
        /** Sets the calendar mode - static, picker, or display */
        get: function () {
            return this._foundation.mode;
        },
        set: function (value) {
            this._foundation.mode = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "disabledDaysOfWeek", {
        /** Allows specified days of the week to be disabled */
        get: function () {
            return this._foundation.disabledDaysOfWeek;
        },
        set: function (value) {
            this._foundation.disabledDaysOfWeek = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "events", {
        /** Sets an array of events to be displayed */
        get: function () {
            return this._foundation.events;
        },
        set: function (value) {
            this._foundation.events = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "showHeader", {
        /** Shows or hides the header */
        get: function () {
            return this._foundation.showHeader;
        },
        set: function (value) {
            this._foundation.showHeader = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "eventsCallback", {
        /** Sets events on dates programmatically */
        set: function (value) {
            this._foundation.eventsCallback = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "disableDayCallback", {
        /** Disables dates programmatically */
        set: function (value) {
            this._foundation.disableDayCallback = value;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(CalendarComponent.prototype, "renderDateCallback", {
        /** Allows content injection on a rendered date */
        set: function (value) {
            this._foundation.renderDateCallback = value;
        },
        enumerable: true,
        configurable: true
    });
    /** Add a new date to selectedDates */
    CalendarComponent.prototype.selectDate = function (date) {
        this._foundation.selectDate(date);
    };
    /** Remove a date from selectedDates */
    CalendarComponent.prototype.removeDate = function (date) {
        this._foundation.removeDate(date);
    };
    /** Add or remove a date from selectedDates */
    CalendarComponent.prototype.toggleDate = function (date) {
        this._foundation.toggleDate(date);
    };
    Object.defineProperty(CalendarComponent.prototype, "dateSelectCallback", {
        /** Sets the callback function for date select events */
        set: function (callback) {
            this._foundation.dateSelectCallback = callback;
        },
        enumerable: true,
        configurable: true
    });
    /** Re-renders any volatile elements. */
    CalendarComponent.prototype.layout = function () {
        this._foundation.layout();
    };
    /**
     * Handles any external key events. Useful when keyboard events from a
     * different element are intended to update state in this component.
     */
    CalendarComponent.prototype.handleKey = function (key, isShiftKey) {
        this._foundation.handleKey(key, isShiftKey);
    };
    /** Sets the actively highlighted date on the current month (only if the date exist on the current visible month). */
    CalendarComponent.prototype.setActiveDate = function (date) {
        this._foundation.setActiveDate(date);
    };
    __decorate([
        FoundationProperty()
    ], CalendarComponent.prototype, "viewIndex", void 0);
    CalendarComponent = __decorate([
        CustomElement({
            name: CALENDAR_CONSTANTS.elementName,
            dependencies: [
                IconButtonComponent,
                ButtonComponent,
                ViewSwitcherComponent,
                ListComponent,
                IconComponent,
                DividerComponent
            ]
        })
    ], CalendarComponent);
    return CalendarComponent;
}(HTMLElement));
export { CalendarComponent };
